// Global App Context for State Management
// Provides seamless data management with optimistic updates

import React, { createContext, useContext, useReducer, useCallback } from 'react';
import { toast } from 'sonner';

// Initial state
const initialState = {
  // Doctors
  doctors: {
    data: [],
    loading: false,
    error: null,
    lastFetch: null,
    pagination: null,
  },
  
  // Bookings
  bookings: {
    data: [],
    loading: false,
    error: null,
    lastFetch: null,
    pagination: null,
  },
  
  // Clinics
  clinics: {
    data: [],
    loading: false,
    error: null,
    lastFetch: null,
    pagination: null,
  },
  
  // UI State
  ui: {
    sidebarOpen: false,
    theme: 'light',
    notifications: [],
  },
  
  // Cache
  cache: {
    doctors: new Map(),
    bookings: new Map(),
    clinics: new Map(),
  }
};

// Action types
const ACTIONS = {
  // Loading states
  SET_LOADING: 'SET_LOADING',
  
  // Doctors
  SET_DOCTORS: 'SET_DOCTORS',
  ADD_DOCTOR: 'ADD_DOCTOR',
  UPDATE_DOCTOR: 'UPDATE_DOCTOR',
  DELETE_DOCTOR: 'DELETE_DOCTOR',
  CACHE_DOCTOR: 'CACHE_DOCTOR',
  
  // Bookings
  SET_BOOKINGS: 'SET_BOOKINGS',
  ADD_BOOKING: 'ADD_BOOKING',
  UPDATE_BOOKING: 'UPDATE_BOOKING',
  DELETE_BOOKING: 'DELETE_BOOKING',
  CACHE_BOOKING: 'CACHE_BOOKING',
  
  // Clinics
  SET_CLINICS: 'SET_CLINICS',
  ADD_CLINIC: 'ADD_CLINIC',
  UPDATE_CLINIC: 'UPDATE_CLINIC',
  DELETE_CLINIC: 'DELETE_CLINIC',
  CACHE_CLINIC: 'CACHE_CLINIC',
  
  // UI
  SET_SIDEBAR_OPEN: 'SET_SIDEBAR_OPEN',
  ADD_NOTIFICATION: 'ADD_NOTIFICATION',
  REMOVE_NOTIFICATION: 'REMOVE_NOTIFICATION',
  
  // Error handling
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
};

// Reducer function
const appReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.SET_LOADING:
      return {
        ...state,
        [action.entity]: {
          ...state[action.entity],
          loading: action.loading,
        }
      };

    case ACTIONS.SET_DOCTORS:
      return {
        ...state,
        doctors: {
          ...state.doctors,
          data: action.data,
          loading: false,
          error: null,
          lastFetch: Date.now(),
          pagination: action.pagination,
        }
      };

    case ACTIONS.ADD_DOCTOR:
      return {
        ...state,
        doctors: {
          ...state.doctors,
          data: [action.doctor, ...state.doctors.data],
        },
        cache: {
          ...state.cache,
          doctors: new Map(state.cache.doctors).set(action.doctor._id, action.doctor),
        }
      };

    case ACTIONS.UPDATE_DOCTOR:
      return {
        ...state,
        doctors: {
          ...state.doctors,
          data: state.doctors.data.map(doctor =>
            doctor._id === action.doctor._id ? { ...doctor, ...action.doctor } : doctor
          ),
        },
        cache: {
          ...state.cache,
          doctors: new Map(state.cache.doctors).set(action.doctor._id, action.doctor),
        }
      };

    case ACTIONS.DELETE_DOCTOR:
      const updatedDoctorsCache = new Map(state.cache.doctors);
      updatedDoctorsCache.delete(action.doctorId);
      return {
        ...state,
        doctors: {
          ...state.doctors,
          data: state.doctors.data.filter(doctor => doctor._id !== action.doctorId),
        },
        cache: {
          ...state.cache,
          doctors: updatedDoctorsCache,
        }
      };

    case ACTIONS.CACHE_DOCTOR:
      return {
        ...state,
        cache: {
          ...state.cache,
          doctors: new Map(state.cache.doctors).set(action.doctor._id, action.doctor),
        }
      };

    case ACTIONS.SET_BOOKINGS:
      return {
        ...state,
        bookings: {
          ...state.bookings,
          data: action.data,
          loading: false,
          error: null,
          lastFetch: Date.now(),
          pagination: action.pagination,
        }
      };

    case ACTIONS.ADD_BOOKING:
      return {
        ...state,
        bookings: {
          ...state.bookings,
          data: [action.booking, ...state.bookings.data],
        },
        cache: {
          ...state.cache,
          bookings: new Map(state.cache.bookings).set(action.booking._id, action.booking),
        }
      };

    case ACTIONS.UPDATE_BOOKING:
      return {
        ...state,
        bookings: {
          ...state.bookings,
          data: state.bookings.data.map(booking =>
            booking._id === action.booking._id ? { ...booking, ...action.booking } : booking
          ),
        },
        cache: {
          ...state.cache,
          bookings: new Map(state.cache.bookings).set(action.booking._id, action.booking),
        }
      };

    case ACTIONS.DELETE_BOOKING:
      const updatedBookingsCache = new Map(state.cache.bookings);
      updatedBookingsCache.delete(action.bookingId);
      return {
        ...state,
        bookings: {
          ...state.bookings,
          data: state.bookings.data.filter(booking => booking._id !== action.bookingId),
        },
        cache: {
          ...state.cache,
          bookings: updatedBookingsCache,
        }
      };

    case ACTIONS.SET_CLINICS:
      return {
        ...state,
        clinics: {
          ...state.clinics,
          data: action.data,
          loading: false,
          error: null,
          lastFetch: Date.now(),
          pagination: action.pagination,
        }
      };

    case ACTIONS.ADD_CLINIC:
      return {
        ...state,
        clinics: {
          ...state.clinics,
          data: [action.clinic, ...state.clinics.data],
        },
        cache: {
          ...state.cache,
          clinics: new Map(state.cache.clinics).set(action.clinic._id, action.clinic),
        }
      };

    case ACTIONS.UPDATE_CLINIC:
      return {
        ...state,
        clinics: {
          ...state.clinics,
          data: state.clinics.data.map(clinic =>
            clinic._id === action.clinic._id ? { ...clinic, ...action.clinic } : clinic
          ),
        },
        cache: {
          ...state.cache,
          clinics: new Map(state.cache.clinics).set(action.clinic._id, action.clinic),
        }
      };

    case ACTIONS.DELETE_CLINIC:
      const updatedClinicsCache = new Map(state.cache.clinics);
      updatedClinicsCache.delete(action.clinicId);
      return {
        ...state,
        clinics: {
          ...state.clinics,
          data: state.clinics.data.filter(clinic => clinic._id !== action.clinicId),
        },
        cache: {
          ...state.cache,
          clinics: updatedClinicsCache,
        }
      };

    case ACTIONS.SET_SIDEBAR_OPEN:
      return {
        ...state,
        ui: {
          ...state.ui,
          sidebarOpen: action.open,
        }
      };

    case ACTIONS.ADD_NOTIFICATION:
      return {
        ...state,
        ui: {
          ...state.ui,
          notifications: [...state.ui.notifications, action.notification],
        }
      };

    case ACTIONS.REMOVE_NOTIFICATION:
      return {
        ...state,
        ui: {
          ...state.ui,
          notifications: state.ui.notifications.filter(n => n.id !== action.id),
        }
      };

    case ACTIONS.SET_ERROR:
      return {
        ...state,
        [action.entity]: {
          ...state[action.entity],
          error: action.error,
          loading: false,
        }
      };

    case ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        [action.entity]: {
          ...state[action.entity],
          error: null,
        }
      };

    default:
      return state;
  }
};

// Create contexts
const AppStateContext = createContext();
const AppDispatchContext = createContext();

// Provider component
export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  return (
    <AppStateContext.Provider value={state}>
      <AppDispatchContext.Provider value={dispatch}>
        {children}
      </AppDispatchContext.Provider>
    </AppStateContext.Provider>
  );
};

// Custom hooks for accessing state and dispatch
export const useAppState = () => {
  const context = useContext(AppStateContext);
  if (!context) {
    throw new Error('useAppState must be used within an AppProvider');
  }
  return context;
};

export const useAppDispatch = () => {
  const context = useContext(AppDispatchContext);
  if (!context) {
    throw new Error('useAppDispatch must be used within an AppProvider');
  }
  return context;
};

// Custom hooks for specific entities
export const useDoctors = () => {
  const state = useAppState();
  const dispatch = useAppDispatch();

  const addDoctor = useCallback((doctor) => {
    dispatch({ type: ACTIONS.ADD_DOCTOR, doctor });
  }, [dispatch]);

  const updateDoctor = useCallback((doctor) => {
    dispatch({ type: ACTIONS.UPDATE_DOCTOR, doctor });
  }, [dispatch]);

  const deleteDoctor = useCallback((doctorId) => {
    dispatch({ type: ACTIONS.DELETE_DOCTOR, doctorId });
  }, [dispatch]);

  const setDoctors = useCallback((data, pagination = null) => {
    dispatch({ type: ACTIONS.SET_DOCTORS, data, pagination });
  }, [dispatch]);

  const setLoading = useCallback((loading) => {
    dispatch({ type: ACTIONS.SET_LOADING, entity: 'doctors', loading });
  }, [dispatch]);

  const getCachedDoctor = useCallback((doctorId) => {
    return state.cache.doctors.get(doctorId);
  }, [state.cache.doctors]);

  return {
    doctors: state.doctors.data,
    loading: state.doctors.loading,
    error: state.doctors.error,
    pagination: state.doctors.pagination,
    addDoctor,
    updateDoctor,
    deleteDoctor,
    setDoctors,
    setLoading,
    getCachedDoctor,
  };
};

export const useBookings = () => {
  const state = useAppState();
  const dispatch = useAppDispatch();

  const addBooking = useCallback((booking) => {
    dispatch({ type: ACTIONS.ADD_BOOKING, booking });
  }, [dispatch]);

  const updateBooking = useCallback((booking) => {
    dispatch({ type: ACTIONS.UPDATE_BOOKING, booking });
  }, [dispatch]);

  const deleteBooking = useCallback((bookingId) => {
    dispatch({ type: ACTIONS.DELETE_BOOKING, bookingId });
  }, [dispatch]);

  const setBookings = useCallback((data, pagination = null) => {
    dispatch({ type: ACTIONS.SET_BOOKINGS, data, pagination });
  }, [dispatch]);

  const setLoading = useCallback((loading) => {
    dispatch({ type: ACTIONS.SET_LOADING, entity: 'bookings', loading });
  }, [dispatch]);

  return {
    bookings: state.bookings.data,
    loading: state.bookings.loading,
    error: state.bookings.error,
    pagination: state.bookings.pagination,
    addBooking,
    updateBooking,
    deleteBooking,
    setBookings,
    setLoading,
  };
};

export const useClinics = () => {
  const state = useAppState();
  const dispatch = useAppDispatch();

  const addClinic = useCallback((clinic) => {
    dispatch({ type: ACTIONS.ADD_CLINIC, clinic });
  }, [dispatch]);

  const updateClinic = useCallback((clinic) => {
    dispatch({ type: ACTIONS.UPDATE_CLINIC, clinic });
  }, [dispatch]);

  const deleteClinic = useCallback((clinicId) => {
    dispatch({ type: ACTIONS.DELETE_CLINIC, clinicId });
  }, [dispatch]);

  const setClinics = useCallback((data, pagination = null) => {
    dispatch({ type: ACTIONS.SET_CLINICS, data, pagination });
  }, [dispatch]);

  const setLoading = useCallback((loading) => {
    dispatch({ type: ACTIONS.SET_LOADING, entity: 'clinics', loading });
  }, [dispatch]);

  return {
    clinics: state.clinics.data,
    loading: state.clinics.loading,
    error: state.clinics.error,
    pagination: state.clinics.pagination,
    addClinic,
    updateClinic,
    deleteClinic,
    setClinics,
    setLoading,
  };
};

// Export actions for direct use if needed
export { ACTIONS };
